import { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import FeedbackCapture from '../feedback/FeedbackCapture';
import CircularProgress from '@mui/material/CircularProgress';
import Sidebar from './Sidebar';
import TopToolbar from './TopToolbar';
import BottomToolbar from './BottomToolbar';
import MobileTabBar, { MobileMoreSheet } from './MobileTabBar';
import MobileMoreMenu from './MobileMoreMenu';
import { TopToolbarProvider } from './TopToolbarContext';
import WelcomeTourHost from '../welcome/WelcomeTourHost';
import WhatsNewNotice from '../welcome/WhatsNewNotice';
import ErrorBoundary from '../common/ErrorBoundary';
import GrannyEasterEgg from '../fun/GrannyEasterEgg';
import SessionLoadError from '../common/SessionLoadError';
import TourListDialog from '../../tours/TourListDialog';
import { useTour } from '../../tours/useTour';
import { useTourStore } from '../../stores/tourStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useGlobalStore } from '../../stores/globalStore';
import { useAlertPolling } from '../../hooks/useAlertPolling';
import { usePreference, PREF_KEYS } from '../../hooks/useUserPreferences';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen';
import {
  useKeyboardShortcuts,
  buildGlobalShortcuts,
  type KeyboardShortcut,
} from '../../hooks/useKeyboardShortcuts';
import { useFacilitySettings } from '../../api/selectiveTouchApi';
import { dismissUpdateShell } from '../../utils/updateShell';
import SiteItemEditorHost from '../../pages/items/SiteItemEditorHost';

// Keep useSearchContext export for backward compatibility (Residents.tsx imports it)
interface SearchContextType {
  globalSearchQuery: string;
  refreshCounter: number;
}

const SearchContext = createContext<SearchContextType>({
  globalSearchQuery: '',
  refreshCounter: 0,
});

export const useSearchContext = () => useContext(SearchContext);

const TABLET_BREAKPOINT = 1024;

/**
 * Rendered beside the page: its effect runs once the page has rendered (not
 * while it is still loading, when the whole tree waits on Suspense). After a
 * reload into a new build that is when the copy of the rail and header painted
 * by index.html fades out (utils/updateShell.ts). Two frames later, so the
 * real frame has been painted underneath first.
 */
function UpdateShellReady() {
  useEffect(() => {
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => dismissUpdateShell());
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);
  return null;
}

function useIsTablet() {
  const [isTablet, setIsTablet] = useState(() => window.innerWidth <= TABLET_BREAKPOINT);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${TABLET_BREAKPOINT}px)`);
    const handler = (e: MediaQueryListEvent) => setIsTablet(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isTablet;
}

export default function MainLayout() {
  const location = useLocation();
  const isTablet = useIsTablet();
  // Auto-collapse on narrow desktops. Deliberately NOT written to the stored
  // preference: widening the window should restore whatever the user chose,
  // rather than silently rewriting it to "collapsed".
  const { isNarrowDesktop, isPhone } = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = usePreference(PREF_KEYS.SIDEBAR_COLLAPSED, false);
  // The page panel at that narrow width (1025-1280px: a small laptop window, and most iPads on their
  // side, 1080 to 1210 wide). It starts closed there, and the toggle opens and closes it. Kept while the
  // window stays narrow, never saved: the saved setting is for wide windows. The toggle used to flip the
  // saved setting, which the narrow width overrode, so on an iPad the expand arrow did nothing on Print
  // Queue and every other page except a Menu Cycle editor (tester, 2026-11-03).
  const [narrowPanelOpen, setNarrowPanelOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // The tab bar steps aside while the on-screen keyboard is up, so it does not sit on the field.
  const keyboardOpen = useKeyboardOpen(isPhone);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const { selectedConnection } = useConnectionStore();
  const userId = useGlobalStore((s) => s.userId);
  const facilityId = useGlobalStore((s) => s.facilityId);
  const noFacilityAccess = useGlobalStore((s) => s.noFacilityAccess);
  const sessionLoadError = useGlobalStore((s) => s.sessionLoadError);
  // Pages wait for the role: every permission check reads 0 until it lands, so rendering earlier
  // would show "no access" and then pop the real page in.
  const roleLoaded = useGlobalStore((s) => s.userRole != null);
  const userEmail = useGlobalStore((s) => s.userEmail);
  const sidebarHidden = useGlobalStore((s) => s.sidebarHidden);
  const sidebarForceCollapsed = useGlobalStore((s) => s.sidebarForceCollapsed);
  // A record screen with its own bottom bar asks for the tab bar to step aside.
  const phoneTabBarHidden = useGlobalStore((s) => s.phoneTabBarHidden);

  // Load facility settings globally so Sidebar/other components know isCorporate, labels, etc.
  const facilitySettingsLoaded = useGlobalStore((s) => s.facilitySettingsLoaded);
  const setFacilitySettings = useGlobalStore((s) => s.setFacilitySettings);
  const facilitySettingsQuery = useFacilitySettings(facilityId);
  useEffect(() => {
    if (!facilitySettingsLoaded && facilitySettingsQuery.data) {
      setFacilitySettings(facilitySettingsQuery.data);
    }
  }, [facilitySettingsLoaded, facilitySettingsQuery.data, setFacilitySettings]);

  // Poll for alerts (new admits, allergy changes, etc.) every 60s
  useAlertPolling();

  // Guided tours
  useTour();
  const loadTourPrefs = useTourStore((s) => s.loadFromPrefs);
  useEffect(() => {
    if (userId > 0) loadTourPrefs(userId);
  }, [userId, loadTourPrefs]);

  const globalShortcuts = useMemo<KeyboardShortcut[]>(
    () => [
      ...buildGlobalShortcuts({
        // Claim Escape only when there is actually a drawer to close. Passing
        // a handler that conditionally does nothing still registered the
        // shortcut, and a registered shortcut swallows the key.
        onEscape: (isTablet && sidebarOpen)
          ? () => setSidebarOpen(false)
          : undefined,
      }),
    ],
    [isTablet, sidebarOpen],
  );

  useKeyboardShortcuts(globalShortcuts);

  // Close sidebar drawer on navigation (tablet/mobile)
  useEffect(() => {
    if (isTablet) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isTablet]);

  const handleToggleSidebar = useCallback(() => {
    if (isTablet) {
      setSidebarOpen((v) => !v);
    } else if (isNarrowDesktop) {
      setNarrowPanelOpen((v) => !v);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  }, [isTablet, isNarrowDesktop, sidebarCollapsed, setSidebarCollapsed]);

  if (!selectedConnection) {
    return <Navigate to="/connection" replace />;
  }

  const isReady = userId > 0 && facilityId > 0 && roleLoaded && !sessionLoadError;
  // Full-screen pages (the sidebar hidden or forced to the rail) run edge to
  // edge on their own layout, so they get no canvas frame around them.
  const pageFlush = sidebarHidden || sidebarForceCollapsed;

  return (
    <TopToolbarProvider>
      <SearchContext.Provider value={{ globalSearchQuery: '', refreshCounter }}>
        {/* Skip to content link for keyboard users (WCAG 2.4.1) */}
        <a className="skip-to-content" href="#main-content">
          Skip to main content
        </a>

        <div
          className="main-layout-grid"
          style={{
            display: 'grid',
            /* `minmax(0, …)`, not a bare `1fr`. A bare `1fr` is
               `minmax(auto, 1fr)`, so the track refuses to shrink below its
               content's min-content width — one wide row anywhere in a page
               (a card with four flag pills, a nowrap table header) stretched
               this track past 100vw and, with it, EVERY item in the column:
               the top toolbar's avatar and the phone tab bar's last tab were
               pushed off-screen and clipped by the `overflow: hidden` below.
               Pinning the minimum to 0 keeps the chrome at viewport width and
               leaves the overflow where it belongs — inside the scrollable
               content pane. */
            gridTemplateColumns: (isTablet || sidebarHidden) ? 'minmax(0, 1fr)' : 'auto minmax(0, 1fr)',
            gridTemplateRows: 'auto minmax(0, 1fr) auto',
            height: '100dvh',
            width: '100vw',
            overflow: 'hidden',
          }}
        >
          {/* Sidebar backdrop (tablet/mobile only) */}
          {isTablet && (
            <div
              className={`sidebar-backdrop${sidebarOpen ? ' visible' : ''}`}
              onClick={() => setSidebarOpen(false)}
              role="presentation"
              aria-hidden="true"
            />
          )}

          {/* Sidebar - hidden in full-screen mode (e.g., Production Menu) */}
          {!sidebarHidden && (
            <div
              className={`main-layout-sidebar${isTablet && sidebarOpen ? ' sidebar-open' : ''}`}
              style={isTablet ? undefined : {
                gridRow: '1 / -1',
              }}
            >
              <ErrorBoundary level="widget" label="Sidebar">
                <Sidebar
                  collapsed={isTablet ? false : isNarrowDesktop ? !narrowPanelOpen : sidebarCollapsed}
                  railOnly={!isTablet && sidebarForceCollapsed}
                  onToggle={handleToggleSidebar}
                  onRefresh={() => setRefreshCounter((c) => c + 1)}
                  drawerOpen={isTablet ? sidebarOpen : undefined}
                />
              </ErrorBoundary>
            </div>
          )}

          <ErrorBoundary level="widget" label="TopToolbar">
            <TopToolbar onMenuClick={isTablet ? () => setSidebarOpen((v) => !v) : undefined} />
          </ErrorBoundary>

          <main
            id="main-content"
            // Named so a screen reader announces "Main content, main landmark"
            // rather than an unnamed region. It is the skip link's target, so
            // it is the first thing heard after skipping the toolbar.
            aria-label="Main content"
            className="main-layout-content"
            style={{
              overflow: 'auto',
              // The canvas: a ground darker than the page, so the page panel
              // below has an edge instead of white running into white. A
              // full-bleed screen (no padding) keeps the page surface.
              backgroundColor: pageFlush ? 'var(--surface-panel)' : 'var(--surface-canvas)',
              // responsive.css narrows this on tablets and phones.
              padding: pageFlush ? 0 : 12,
            }}
            tabIndex={-1}
          >
            {/* The page panel: every page sits on one bordered sheet. Pages
                whose root is `.page-bare` (cards laid straight on the canvas,
                like the dashboard) drop the sheet; see globalStyles.css. */}
            <div className={`page-panel${pageFlush ? ' page-panel--flush' : ''}`}>
              {sessionLoadError ? (
                <>
                  <SessionLoadError message={sessionLoadError} />
                  <UpdateShellReady />
                </>
              ) : isReady ? (
                <>
                  <ErrorBoundary level="page" label="PageContent">
                    {/* Items open over the page from anywhere on it, never in a new tab. */}
                    <SiteItemEditorHost>
                      <Outlet />
                    </SiteItemEditorHost>
                  </ErrorBoundary>
                  <UpdateShellReady />
                </>
              ) : noFacilityAccess ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    gap: 12,
                    maxWidth: 560,
                    margin: '0 auto',
                    textAlign: 'center',
                    padding: 24,
                  }}
                  role="alert"
                >
                  <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>No facility access</h2>
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--font-size-base)' }}>
                    Your account ({userEmail || 'this user'}) is not assigned to a facility in this database.
                    Please contact your administrator to grant facility access.
                  </p>
                  <UpdateShellReady />
                </div>
              ) : (
                <div
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}
                  role="status"
                  aria-label="Loading application"
                >
                  <CircularProgress size={36} aria-hidden="true" />
                  <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }}>Loading...</span>
                </div>
              )}
            </div>
          </main>

          {/* On a phone the status strip gives way to a tab bar. Both at
              once would take a quarter of a 390x844 viewport, and only one of
              them is something a thumb reaches for — the strip's contents
              (connection, support, version) move into the More sheet.
              isPhone, not isMobile: a phone on its side is 844px wide but
              only 390px tall, and kept the strip under the page. */}
          {isPhone ? (
            <>
              {!keyboardOpen && !phoneTabBarHidden && <MobileTabBar onMore={() => setMoreOpen(true)} moreOpen={moreOpen} />}
              <MobileMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)}>
                <MobileMoreMenu onNavigate={() => setMoreOpen(false)} />
              </MobileMoreSheet>
            </>
          ) : (
            <BottomToolbar />
          )}

          {/* The welcome tour, once, the first time someone signs in (and again from Help). */}
          {isReady && <WelcomeTourHost />}
          {/* A release note flagged to announce, once, after the tour (src/releaseNotes). */}
          {isReady && <WhatsNewNotice />}
          <TourListDialog />

          {/* Test Kitchen passive capture — error listeners, breadcrumbs, coverage pings.
              Renders nothing; the visible trigger lives in TopToolbar. */}
          <FeedbackCapture />

          {/* PSI staff only: a cartoon grandmother when the X-Large size is chosen. */}
          <GrannyEasterEgg />
        </div>
      </SearchContext.Provider>
    </TopToolbarProvider>
  );
}
